import { Flex } from "@/components/atoms/flex";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import { mergeClasses, Divider } from "@fluentui/react-components";
import { useCallback, useState, useRef, useEffect } from "react";
import { formatDate } from "@/common/helpers/dateHelper";
import {
	AgentState,
	useAgentStore,
} from "@/common/stores/persistingStores/agentPermissions";
import { Note } from "@/components/organisms/history/types";

interface InteractionNoteItemProps {
	note: Note;
	isLast: boolean;
}

export const InteractionNoteItem = (props: InteractionNoteItemProps) => {
	const { note, isLast } = props;
	const intl = useIntl();
	const classes = useStyle();
	const agentState = useAgentStore((state: AgentState) => state);
	const [isNoteExpanded, setIsNoteExpanded] = useState(false);
	const textRef = useRef<HTMLDivElement>(null);
	const [isShowMoreNeeded, setIsShowMoreNeeded] = useState(false);

	useEffect(() => {
		if (textRef.current) {
			const el = textRef.current;

			setIsShowMoreNeeded(el.scrollHeight > el.clientHeight);
		}
	}, [note.note]);

	const buildNoteTitleSuffix = useCallback(
		(interactionNote: Note) => {
			if (!interactionNote.creationTime) {
				return "";
			}

			const date = formatDate(interactionNote.creationTime);

			//#warning-ts: interactionNote.agentId should be used, but it should not be saved as Guid.Empty some times ...
			if (agentState.fullName === interactionNote.agentName) {
				return `(${intl.formatMessage({ id: "Notes.Me" })}) - ${date}`;
			} else {
				return ` - ${date}`;
			}
		},
		[agentState]
	);

	return (
		<Flex column>
			<Flex className={classes.noteTitle}>
				{note.isWrapUp && (
					<span style={{ fontStyle: "italic" }}>
						{intl.formatMessage({ id: "Notes.IsWrapUp" })}:&nbsp;
					</span>
				)}
				<span
					className={mergeClasses(classes.ellipsis, classes.noteAuthorName)}
					title={note.agentName}
				>
					{note.agentName}
				</span>
				<span>&nbsp;{buildNoteTitleSuffix(note)}</span>
			</Flex>

			<div className={classes.noteTextContainer}>
				<div
					ref={textRef}
					className={`${classes.noteText} ${
						isNoteExpanded ? classes.expanded : ""
					}`}
				>
					{note.note}
				</div>
				{isShowMoreNeeded && !isNoteExpanded && (
					<button
						onMouseDown={(e) => e.preventDefault()}
						className={classes.showMore}
						style={{ display: isNoteExpanded ? "none" : "inline-block" }}
						onClick={() => setIsNoteExpanded(true)}
					>
						{!isNoteExpanded && intl.formatMessage({ id: "Notes.ShowMore" })}
					</button>
				)}
			</div>
			{!isLast && <Divider className={classes.noteTextContainer} />}
		</Flex>
	);
};
